// Contracts shared by the server, the TV client and the mobile controller.
// Everything that crosses the wire is described here.

export type DeviceKind = 'tv' | 'mobile' | 'desktop';
export type PlayMode = 'tv_mobile' | 'tv_only' | 'mobile_first';
export type SessionStatus = 'lobby' | 'playing' | 'ended';

export interface Cover {
  emoji: string;
  gradient: [string, string];
  imageUrl?: string | null;
}

export interface PlayerPublic {
  id: string;
  name: string;
  avatar: string;
  color: string;
  connected: boolean;
  joinedAt: number;
}

export interface SessionGameInfo {
  id: string;
  title: string;
  engine: string;
  cover: Cover;
  minPlayers: number;
  maxPlayers: number;
  playMode: PlayMode;
}

export interface RankingEntry {
  playerId: string;
  name: string;
  score: number;
  rank: number;
  /** Team games: the player's team, so records can tell 2v2 from 3v3 and who played on the same side. */
  team?: string;
}

export interface GameResultPublic {
  ranking: RankingEntry[];
  summary?: string;
  /** The host ended the game early: the ranking is the standing at that moment, not a final result. */
  aborted?: boolean;
}

export interface SessionPublic {
  id: string;
  code: string;
  status: SessionStatus;
  game: SessionGameInfo;
  players: PlayerPublic[];
  hostConnected: boolean;
  /** Extra presentation screens (other TVs) watching this room right now. */
  displays: number;
  createdAt: number;
  result: GameResultPublic | null;
  /** Host-adjustable settings (rounds, timers...) for the next game. Null when the engine has none. */
  settings: SessionSettingsPublic | null;
  /** Informational message for the room, e.g. after a server restart interrupted a game. */
  notice: string | null;
  /**
   * Game clock. Engine timestamps (endsAt, deadlines) are on a clock that stops while paused:
   * gameTime = serverTime - total, frozen at pausedAt - total while paused.
   */
  pause: { pausedAt: number | null; total: number };
}

/** short/normal/long pick duration-optimised values for the current player count; custom = host's own values. */
export type SessionPace = 'short' | 'normal' | 'long' | 'custom';
export type OptionValue = number | boolean | string;

export interface HostOptionDto {
  key: string;
  label: string;
  type: 'number' | 'bool' | 'select';
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: { value: string; label: string }[];
}

export interface SessionSettingsPublic {
  pace: SessionPace;
  values: Record<string, OptionValue>;
  options: HostOptionDto[];
  estimatedMinutes: number;
}

/** Sent to every socket whenever the session or the game state changes. `view` is personalised per viewer. */
export interface StateMessage<V = unknown> {
  session: SessionPublic;
  view: V | null;
  role: 'host' | 'player';
  playerId: string | null;
  version: number;
  serverTime: number;
}

/** Transient events used for animations/sounds (dice rolls, buzzers...). They are not part of the state. */
export interface GameEvent {
  type: string;
  [key: string]: unknown;
}

export interface ClientAction {
  type: string;
  payload?: unknown;
}

export type HostCommand =
  | { type: 'start' }
  | { type: 'end' }
  | { type: 'restart' }
  | { type: 'kick'; playerId: string }
  | { type: 'action'; action: ClientAction }
  | { type: 'settings'; pace?: SessionPace; values?: Record<string, OptionValue> }
  | { type: 'pause' }
  | { type: 'resume' };

export interface AckResponse {
  ok: boolean;
  error?: string;
}

export interface ClockSyncResponse {
  clientTime: number;
  serverTime: number;
}

/** Socket.IO event names. */
export const EVT = {
  state: 'state',
  event: 'game:event',
  kicked: 'session:kicked',
  closed: 'session:closed',
  error: 'session:error',
  playerAction: 'player:action',
  hostCommand: 'host:command',
  clockSync: 'clock:sync',
} as const;

export const AVATARS = ['🦊', '🐼', '🐸', '🦁', '🐙', '🦄', '🐯', '🐧', '🐨', '🐵', '🦉', '🐳', '🐲', '🦖', '🐝', '🦋'];

export const PLAYER_COLORS = [
  '#ff5c7a', '#4dabff', '#ffc53d', '#3ddc97', '#b37bff', '#ff8f3d',
  '#2fd3e0', '#f364d3', '#9be15d', '#ff6b4a', '#7c8cff', '#e0c068',
];
